import React, { useEffect, useState } from 'react';
import { keyframes } from '@emotion/react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { getAllPlans, fetchInvoices, fetchActiveSubscription } from '~/redux/actions/plans-actions';
import { failure } from '~/redux/actions/snackbar-actions';
import { getUserData } from '~/redux/actions/auth-actions';

// Utils
import { parseJson, transformData } from '~/utils/helperFunctions';
import { MESSAGES } from '~/utils/message';

// Components
import StarterPlan from '~/components/Plan/StarterPlan';
import PlanCard from '~/components/Plan/PlanCard';
import ProductTable from '~/components/Plan/ProductTable';
import UpgradeModal from './UpgradeModal';
import DowngradeModal from './DowngradeModal';
import { getLocalPreviewPlans } from './previewPlans';
import Loader from '~/components/General/Loader';

// MUI Components
import { Box, Container, Divider, Grid, Typography } from '@mui/material';

// Icons
import { StaterCIcon, GrowthCIcon, ProfessionalCIcon, EnterpriseCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const planImages = {
  0: StaterCIcon,
  1: GrowthCIcon,
  2: ProfessionalCIcon,
  3: EnterpriseCIcon,
};
const STANDARD_PLAN_SLUGS = ['starter', 'stater', 'growth', 'professional'];
const PLAN_ORDER = [...STANDARD_PLAN_SLUGS, 'enterprise'];
const PLAN_DURATION_MATCHES = {
  monthly: false,
  yearly: true,
  annually: true,
  month: false,
  year: true,
};

const index = () => {
  const [plans, setPlans] = useState({ year: [], month: [] });
  const [activePlan, setActivePlan] = useState('monthly');
  const [pricing, setPricing] = useState({ year: [], month: [] });
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [downgradeModal, setDowngradeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [planRenewsAt, setPlansRenewsAt] = useState('');
  const [planLoader, setPlanLoader] = useState(true);
  const [isPreviewingPlans, setIsPreviewingPlans] = useState(false);

  const dispatch = useDispatch();

  const invoice = useSelector((state) => state.stripeReducers.invoices) || [];
  const [selectedPlan, setSelectedPlan] = useState({ type: null });

  const currentPlan = useSelector((state) => state.userReducers.user?.subscribedPlan);

  const currentPlanStripId = useSelector(
    (state) => state.userReducers.user?.subscribedPlan?.stripeId
  );

  const currentUser = useSelector((state) => state.userReducers.user);

  const currentPlanType = Object.keys(plans).find((key) =>
    plans[key].some((plan) => plan?.stripeId === currentPlanStripId)
  );

  const currentPlanDetails = Object.keys(plans)
    .map((el) => plans[el].find((plan) => plan?.stripeId === currentPlanStripId))
    .filter((plan) => plan !== undefined);
  const activePlans = activePlan === 'yearly' ? plans.year : plans.month;
  const isCurrentPlanLoaded = Boolean(currentPlan);
  const isActivePlanYearly = activePlan === 'yearly';
  const currentPlanMatchesActiveInterval =
    PLAN_DURATION_MATCHES[currentPlan?.durationType] === isActivePlanYearly;
  const planSkeletons =
    currentPlan &&
    !STANDARD_PLAN_SLUGS.includes(currentPlan?.slug) &&
    currentPlanMatchesActiveInterval
      ? ['1', '2', '3', '4']
      : ['1', '2', '3'];
  const hasActivePlans = activePlans.length > 0;
  const activePlansHaveCurrentPlan =
    !currentPlanMatchesActiveInterval ||
    (currentPlanStripId
      ? activePlans.some((plan) => plan?.stripeId === currentPlanStripId)
      : activePlans.length >= planSkeletons.length);
  const canShowExistingPlanCards = hasActivePlans && activePlansHaveCurrentPlan;
  const showInitialPlanLoader = planLoader && !isCurrentPlanLoaded && !hasActivePlans;
  const showPlanSkeleton = planLoader && isCurrentPlanLoaded && !canShowExistingPlanCards;
  const planCardsLayoutClass =
    (showPlanSkeleton ? planSkeletons.length : activePlans.length) >= 4
      ? 'fourPlans'
      : 'threePlans';

  const showLocalPreviewPlans = () => {
    if (import.meta.env.VITE_APP_ENV !== 'local') return;

    setPlans(getLocalPreviewPlans());
    setPricing({ year: [], month: [] });
    setIsPreviewingPlans(true);
  };

  useEffect(() => {
    if (selectedPlan && selectedPlan.type == 'UPGRADE') {
      setUpgradeModal(true);
    } else if (selectedPlan && selectedPlan.type == 'DOWNGRADE') {
      setDowngradeModal(true);
    }
  }, [selectedPlan]);

  const sortPlans = (array) => {
    return array.sort((a, b) => {
      const indexA = PLAN_ORDER.indexOf(a.slug);
      const indexB = PLAN_ORDER.indexOf(b.slug);

      return indexA - indexB;
    });
  };

  const getInvoices = async (payload = null) => {
    try {
      setLoading(true);
      await dispatch(fetchInvoices(payload));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message));
      return error;
    } finally {
      setLoading(false);
    }
  };

  const getSubscribtionDetails = async () => {
    try {
      const response = await fetchActiveSubscription();
      if (response.status === 200) {
        setPlansRenewsAt(response.data.data.planRenewsAt);
      }
    } catch (error) {
      return error;
    }
  };

  const getAllPlansFromServer = async () => {
    try {
      setPlanLoader(true);
      const response = await getAllPlans();
      const planRows = response?.data?.data?.rows;

      if (response?.status !== 200 || !planRows?.some((item) => item.id)) {
        showLocalPreviewPlans();
        return;
      }

      if (response.status === 200) {
        setIsPreviewingPlans(false);
        const plansByInterval = planRows.reduce((accumulator, item) => {
          if (item.id) {
            const interval = item.meta?.stripeMeta?.interval;

            if (!accumulator[interval]) {
              accumulator[interval] = [];
            }
            const isYearly = interval === 'year' ? true : false;
            const amount = (+item.amount).toFixed();
            const perMonth = isYearly ? (amount / 12).toFixed(2) : amount;
            accumulator[interval].push({
              ...item,
              pricing: [
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_1) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_2) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_3) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_4) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_5) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_6) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_7) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_8) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_9) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_10) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_11) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_12) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_13) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_14) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_15) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_16) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_17) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_18) },
                { ...parseJson(item.meta.stripeMeta.metadata.pricing_19) },
              ],
              features: {
                users: {
                  ...parseJson(item.meta.stripeMeta.metadata.features_users),
                },
                contacts: {
                  ...parseJson(item.meta.stripeMeta.metadata.features_contacts),
                },
                customFields: {
                  ...parseJson(item.meta.stripeMeta.metadata.features_custom_fields),
                },
                templates: {
                  ...parseJson(item.meta.stripeMeta.metadata.features_stored_custom_templates),
                },
                webhooks: {
                  ...parseJson(item.meta.stripeMeta.metadata.features_webhooks),
                },
                tags: {
                  ...parseJson(item.meta.stripeMeta.metadata.features_tags),
                },
                mailingLimit: {
                  ...parseJson(item.meta.stripeMeta.metadata.features_monthly_mailings_limit),
                },
                mailTracking: { limit: 1, show: true },
                userRolesPermissions: {
                  ...parseJson(item.meta.stripeMeta.metadata.general_user_roles_permissions),
                },
                ayalyticsDashboard: { limit: 1, show: true },
                scheduleMailing: {
                  ...parseJson(item.meta.stripeMeta.metadata.general_scheduled_mail),
                },
                zapier: { limit: 1, show: true },
                goHighLevel: { limit: 1, show: true },
                hubspot: {
                  limit: item.slug === 'professional' || item.slug === 'growth' ? 1 : 0,
                  show: true,
                },
                shopify: {
                  limit: item.slug === 'professional' || item.slug === 'growth' ? 1 : 0,
                  show: true,
                },
                sdk: {
                  limit: item.slug === 'professional' || item.slug === 'growth' ? 1 : 0,
                  show: true,
                },
              },
              featuresV2Transformer: item.features.reduce((acc, feat) => {
                acc[feat.slug] = {
                  show: feat.show ?? true,
                  title: feat.label || feat.slug || '',
                  value: feat.value,
                };
                return acc;
              }, {}),
              featuresV2: item.features,
              plan: item.amount === '0.00' ? 'Free' : `$${perMonth}/mo`,
              billAnnual: isYearly ? `$${amount} billed annually` : '',
              billMonthly: '',
              isActive: false,
              isYearly: isYearly,
            });
          }
          return accumulator;
        }, {});

        const _plans = {
          year: sortPlans(plansByInterval['year']),
          month: sortPlans(plansByInterval['month']),
        };

        _plans.year = _plans.year.map((item, index) => {
          return {
            ...item,
            billMonthly: _plans?.month[index]?.plan || '',
          };
        });

        const dummyEnterpriseMonthly = planRows.find(
          (item) => !item.id && item.durationType === 'monthly'
        );
        const dummyEnterpriseYearly = planRows.find(
          (item) => !item.id && item.durationType === 'annually'
        );

        const pricing = {
          year: createProductData(_plans.year),
          month: createProductData(_plans.month),
        };
        setPricing(pricing);
        const customMonthlyPlan = assignedCustomPlan(false, dummyEnterpriseMonthly);
        const customYearlyPlan = assignedCustomPlan(true, dummyEnterpriseYearly);

        if (customMonthlyPlan) {
          _plans.month.push(customMonthlyPlan);
        }

        if (customYearlyPlan) {
          _plans.year.push(customYearlyPlan);
        }

        setPlans(_plans);
      }
    } catch (error) {
      showLocalPreviewPlans();
      return error;
    } finally {
      setPlanLoader(false);
    }
  };

  const enterprisePlan = (isYearly, dummyPlan) => {
    try {
      if (!dummyPlan) {
        return null;
      }

      const interval = {
        monthly: false,
        yearly: true,
      };
      const data = {
        image: planImages[3],
        name: `Enterprise ${isYearly ? '(Annually)' : '(Monthly)'}`,
        features: {
          users: { limit: -1, show: true },
          contacts: { limit: -1, show: true },
          templates: { limit: -1, show: true },
          customFields: { limit: -1, show: true },
          tags: { limit: -1, show: true },
          webhooks: { limit: -1, show: true },
          mailingLimit: { limit: -1, show: true },
          mailTracking: { limit: 1, show: true },
          userRolesPermissions: { limit: 1, show: true },
          ayalyticsDashboard: { limit: 1, show: true },
          scheduleMailing: { limit: 1, show: true },
          zapier: { limit: 1, show: true },
          goHighLevel: { limit: 1, show: true },
          hubspot: { limit: 1, show: true },
          shopify: { limit: 1, show: true },
          sdk: { limit: 1, show: true },
        },
        plan: 'Talk to Sales',
        billAnnual: '',
        billMonthly: '',
        isActive: false,
        isYearly: isYearly,
        stripeId: null,
        featuresV2Transformer: dummyPlan.features.reduce((acc, feat) => {
          acc[feat.slug] = {
            show: feat.show ?? true,
            title: feat.label || feat.slug || '',
            value: feat.value,
          };
          return acc;
        }, {}),
        featuresV2: dummyPlan.features,
      };
      if (
        !STANDARD_PLAN_SLUGS.includes(currentPlan?.slug) &&
        interval[currentPlan?.durationType] === isYearly
      ) {
        const planData = transformData(currentPlan.meta.stripeMeta.metadata);
        data.name = currentPlan?.name;
        data.features = {
          ...data.features,
          users: { ...planData.features_users },
          contacts: { ...planData.features_contacts },
          templates: { ...planData.features_stored_custom_templates },
          customFields: { ...planData.features_custom_fields },
          tags: { ...planData.features_tags },
          webhooks: { ...planData.features_webhooks },
          mailingLimit: { ...planData.features_monthly_mailings_limit },
          userRolesPermissions: { ...planData.general_user_roles_permissions },
          scheduleMailing: { ...planData.general_scheduled_mail },
        };
        data.stripeId = currentPlan.stripeId;
        data.isActive = true;
        ((data.featuresV2Transformer = currentUser?.subscribedPlan?.features?.reduce(
          (acc, feat) => {
            acc[feat.slug] = {
              show: feat.show ?? true,
              title: feat.label || feat.slug || '',
              value: feat.value,
            };
            return acc;
          },
          {}
        )),
          (data.featuresV2 = currentUser?.subscribedPlan?.features || currentUser?.features));
      }
      return data;
    } catch (error) {
      return error;
    } finally {
    }
  };

  const assignedCustomPlan = (isYearly, dummyPlan) => {
    const isStandardPlan = STANDARD_PLAN_SLUGS.includes(currentPlan?.slug);
    const isAssignedInterval = PLAN_DURATION_MATCHES[currentPlan?.durationType] === isYearly;

    if (!currentPlan || isStandardPlan || !isAssignedInterval) {
      return null;
    }

    return enterprisePlan(isYearly, dummyPlan);
  };

  const createProductData = (data) => {
    const output = {};
    data.forEach((plan) => {
      plan.pricing.forEach((item) => {
        const title = item.title;
        const limit = item?.limit?.toFixed(3);

        if (!output[title]) {
          output[title] = {};
        }

        output[title][plan.slug] = limit;
      });
    });
    // Convert output object to the desired format
    const finalOutput = Object.keys(output).map((title) => ({
      name: title,
      starter: output[title]['starter'] || 0,
      growth: output[title]['growth'] || 0,
      professional: output[title]['professional'] || 0,
      enterprise: output[title]['enterprise'] || 'Contact Sales',
    }));
    return finalOutput;
  };

  const handleOpen = (stripeId) => {
    let selected = (activePlan === 'yearly' ? plans.year : plans.month).find(
      (el) => el.stripeId === stripeId
    );
    setUpgradeModal(true);
    setSelectedPlan({ ...selected, type: 'UPGRADE' });
  };

  const handleClose = () => {
    setUpgradeModal(false);
    setSelectedPlan({});
  };

  const handleDowngradeOpen = (stripeId) => {
    let selected = (activePlan === 'yearly' ? plans.year : plans.month).find(
      (el) => el.stripeId === stripeId
    );
    setSelectedPlan({ ...selected, type: 'DOWNGRADE' });
  };

  const handleDowngradeClose = () => {
    setDowngradeModal(false);
    setSelectedPlan({});
  };

  useEffect(() => {
    if (currentPlan) {
      getAllPlansFromServer();
    } else {
      showLocalPreviewPlans();
    }
  }, [JSON.stringify(currentPlan)]);

  useEffect(() => {
    dispatch(getUserData()).then((response) => {
      if (!response?.data?.data?.subscribedPlan) {
        setPlanLoader(false);
      }
    });
    getSubscribtionDetails();
    getInvoices();
  }, []);

  const skeletonAnimation = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

  const planCardsContent = (() => {
    if (showInitialPlanLoader) {
      return (
        <Box className="plansCircularLoader">
          <Loader />
        </Box>
      );
    }

    if (showPlanSkeleton) {
      return planSkeletons.map((val) => {
        return (
          <Box
            key={val}
            className="plansCardSkeleton"
            sx={{
              animation: `${skeletonAnimation} 3.5s linear infinite`,
            }}
          />
        );
      });
    }

    return activePlans.map((card, index) => {
      return (
        <PlanCard
          plansOrder={activePlans}
          image={planImages[index]}
          title={card.name}
          features={card.features}
          featuresV2={card.featuresV2}
          plan={card.plan}
          billAnnual={card.billAnnual}
          billMonthly={card.billMonthly}
          key={index}
          stripeId={card.stripeId}
          annual={card.isYearly}
          selectedType={card.slug}
          isActive={card.isActive}
          currentPlanStripId={currentPlanStripId}
          currentPlanType={currentPlanType}
          selectedPlanType={card.isYearly ? 'year' : 'month'}
          handleOpen={handleOpen}
          handleDowngradeOpen={handleDowngradeOpen}
          currentPlan={currentPlan}
          setPlanDetails={setPlanDetails}
          previewMode={isPreviewingPlans}
        />
      );
    });
  })();

  return (
    <>
      <Box className="olcWrapper planPageWrapper">
        <Box className="planWrapper olcScreenCard">
          <Grid container spacing={0}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Container maxWidth="xxl" className="planSectionContainer">
                <Grid container>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <StarterPlan
                      activePlan={activePlan}
                      setActivePlan={setActivePlan}
                      currentPlan={currentPlan}
                      planRenewsAt={planRenewsAt}
                    />
                  </Grid>
                </Grid>
            </Container>
            <Divider className="divider" />
            <Container maxWidth="xxl" sx={{ padding: '0 !important' }}>
              <Box className="availableHeading">
                <Typography variant="h6">
                  {MESSAGES.ACCOUNT.PLANS.AVAILABLE_PLANS_HEADING}
                </Typography>
              </Box>
              <Box className={`cardGridWrapper ${planCardsLayoutClass}`}>
                {planCardsContent}
              </Box>
            </Container>
            {/* <Divider className="divider" />
            <Container maxWidth="xxl">
              <Box className="availableHeading">
                <Typography variant="h6">
                  {MESSAGES.ACCOUNT.PLANS.PRODUCT_PRICING_HEADING}
                </Typography>
              </Box>
              <Grid container>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <ProductTable
                    data={activePlan === 'yearly' ? pricing.year : pricing.month}
                    tableType="product"
                  />
                </Grid>
              </Grid>
            </Container> */}
              <Divider className="divider" />
              <Container maxWidth="xxl" className="planSectionContainer">
                <Box className="availableHeading">
                  <Typography variant="h6">
                    {MESSAGES.ACCOUNT.PLANS.INVOICE.HEADING}
                  </Typography>
                </Box>
                <Grid container>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <ProductTable
                      tableType="invoice"
                      data={invoice}
                      callback={getInvoices}
                      loading={loading}
                    />
                  </Grid>
                </Grid>
              </Container>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {upgradeModal && (
        <UpgradeModal
          open={upgradeModal}
          handleClose={handleClose}
          selectedPlan={selectedPlan}
          planDetails={planDetails}
        />
      )}
      {downgradeModal && (
        <DowngradeModal
          open={downgradeModal}
          handleClose={handleDowngradeClose}
          selectedPlan={selectedPlan}
          currentPlanDetails={currentPlanDetails[0]}
          planDetails={planDetails}
        />
      )}
    </>
  );
};

export default index;
